import { useQuery } from '@apollo/client'
import { useRef, useState } from 'react'

import {
  GET_ELD_ASSET_LOCATION,
  type IELDApp,
  type IELDAsset,
  type IELDAssetLocation,
  type IGetELDAssetLocationResponse
} from '../../../../../../data/queries/eld-apps'

interface IUseELDAssetLocationPreviewParams {
  assetType: 'Trailer' | 'Truck'
  selectedELDApp: IELDApp | null
}

interface IELDAssetLocationPreviewState {
  assetNumber: string | null
  hasNoLocation: boolean
  isLoading: boolean
  location: IELDAssetLocation | null
}

interface ILocationCacheEntry {
  data: IELDAssetLocation | null
  timestamp: number
}

/** Cache TTL: 2 minutes - locations can change frequently, but we don't need to refetch on every click */
const LOCATION_CACHE_TTL = 2 * 60 * 1000
const locationCache = new Map<string, ILocationCacheEntry>()

/**
 * Clears the location cache. Useful for testing.
 */
export const clearLocationCache = (): void => {
  locationCache.clear()
}

/**
 * Generates a unique cache key for an asset location
 */
const getLocationCacheKey = (
  appType: string,
  workspaceID: string,
  assetType: string,
  assetNumber: string,
  assetID?: string | null
): string => {
  return `${appType}-${workspaceID}-${assetType}-${assetNumber}-${assetID || 'null'}`
}

/**
 * Gets cached location if it exists and is not expired
 */
const getCachedLocation = (cacheKey: string): IELDAssetLocation | null | undefined => {
  const cached = locationCache.get(cacheKey)

  if (!cached) {
    return undefined // No cache entry
  }

  const now = Date.now()
  const isExpired = now - cached.timestamp > LOCATION_CACHE_TTL

  if (isExpired) {
    locationCache.delete(cacheKey)
    return undefined // Cache expired
  }

  return cached.data // Returns null if location was fetched but not found
}

/**
 * Caches a location result
 */
const cacheLocation = (cacheKey: string, data: IELDAssetLocation | null): void => {
  locationCache.set(cacheKey, {
    data,
    timestamp: Date.now()
  })
}

export const useELDAssetLocationPreview = ({ assetType, selectedELDApp }: IUseELDAssetLocationPreviewParams) => {
  const [previewingAssetNumber, setPreviewingAssetNumber] = useState<string | null>(null)
  const [previewedLocation, setPreviewedLocation] = useState<IELDAssetLocation | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const latestRequestRef = useRef<string | null>(null)

  const { refetch } = useQuery<IGetELDAssetLocationResponse>(GET_ELD_ASSET_LOCATION, {
    fetchPolicy: 'network-only',
    skip: true
  })

  const previewState: IELDAssetLocationPreviewState = {
    assetNumber: previewingAssetNumber,
    hasNoLocation: !!previewingAssetNumber && !isLoading && previewedLocation === null,
    isLoading,
    location: previewedLocation
  }

  const clearPreview = () => {
    setPreviewingAssetNumber(null)
    setPreviewedLocation(null)
    latestRequestRef.current = null
  }

  const previewLocation = async (asset: IELDAsset) => {
    if (!selectedELDApp) return

    const cacheKey = getLocationCacheKey(
      selectedELDApp.appType,
      selectedELDApp.workspaceID,
      assetType,
      asset.assetNumber,
      asset.assetID
    )

    // Check cache first
    const cachedData = getCachedLocation(cacheKey)
    if (cachedData !== undefined) {
      // We have a cached result (could be null if location wasn't found)
      setPreviewingAssetNumber(asset.assetNumber)
      setPreviewedLocation(cachedData)
      setIsLoading(false)
      return
    }

    // No cache hit, fetch from network
    const requestKey = `${asset.assetNumber}-${Date.now()}`
    latestRequestRef.current = requestKey

    setPreviewingAssetNumber(asset.assetNumber)
    setPreviewedLocation(null)
    setIsLoading(true)

    try {
      const result = await refetch({
        appType: selectedELDApp.appType,
        assetID: asset.assetID || null,
        assetNumber: asset.assetNumber,
        assetType,
        workspaceID: selectedELDApp.workspaceID
      })

      if (latestRequestRef.current !== requestKey) {
        return
      }

      const fetchedLocation = result.data?.getELDAssetLocation ?? null

      // Cache the result
      cacheLocation(cacheKey, fetchedLocation)

      setPreviewedLocation(fetchedLocation)
    } finally {
      if (latestRequestRef.current === requestKey) {
        setIsLoading(false)
      }
    }
  }

  return {
    clearPreview,
    previewLocation,
    previewState
  }
}
